import { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery
} from '@mui/material';
import { Table } from '../../../components';
import { IDropdownResponse, IJobReassignment } from '../../../models';
import {
  getJobReassignments,
  getProjectManagers,
  getSupervisors,
  updateJobReassignments
} from '../../../fetch';
import { formatDate } from '../../../helpers';
import { AssignmentInd, Replay } from '@mui/icons-material';
import { deepEqual } from 'fast-equals';
import { MobileStaffReassignment } from './mobile-staff-reassignment-list';
import { StaffReassignmentFilters } from './staff-reassignment-filters';

export const StaffReassignmentList = () => {
  const { enqueueSnackbar } = useSnackbar();

  const isMobile = useMediaQuery('(max-width: 960px)');

  const [isLoadingJobReassignments, setLoadingJobReassignments] = useState<boolean>(true);
  const [jobReassignments, setJobReassignments] = useState<IJobReassignment[]>([]);
  const [updatedJobReassignments, setUpdatedJobReassignments] = useState<IJobReassignment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [selectedProjectManager, setSelectedProjectManager] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [filters, setFilters] = useState<{
    projectManagerId?: number | null;
    supervisorId?: number | null;
  } | null>({});

  const fetchJobReassignments = async () => {
    setLoadingJobReassignments(true);
    try {
      const res = await getJobReassignments({
        sortBy: 'NeighborhoodDescription',
        sortDirection: 'Asc',
        ...filters
      });
      const formattedData = res.records.map(item => {
        return { ...item, newProjectManager: '', newSupervisor: '' };
      });
      setJobReassignments(formattedData);
      setUpdatedJobReassignments(formattedData);
    } catch (error) {
      enqueueSnackbar(`Error loading job reassignments, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingJobReassignments(false);
    }
  };

  const [isLoadingProjectManagers, setLoadingProjectManagers] = useState<boolean>(true);
  const [projectManagers, setProjectManagers] = useState<IDropdownResponse[]>([]);
  const fetchProjectManagers = async () => {
    setLoadingProjectManagers(true);
    try {
      const res = await getProjectManagers();
      const formattedData = res.records.map(item => {
        return {
          value: item.projectManagerId.toString(),
          description: item.displayName,
          shorthand: item.projectManagerId.toString()
        };
      });
      setProjectManagers(formattedData);
    } catch (error) {
      enqueueSnackbar(`Error loading project managers, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingProjectManagers(false);
    }
  };

  const [isLoadingSupervisors, setLoadingSupervisors] = useState<boolean>(true);
  const [supervisors, setSupervisors] = useState<IDropdownResponse[]>([]);
  const fetchSupervisors = async () => {
    setLoadingSupervisors(true);
    try {
      const res = await getSupervisors();
      const formattedData = res.records.map(item => {
        return {
          value: item.supervisorId.toString(),
          description: item.displayName,
          shorthand: item.supervisorId.toString()
        };
      });
      setSupervisors(formattedData);
    } catch (error) {
      enqueueSnackbar(`Error loading supervisors, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const handleOnChange = (
    value: number | string,
    type: 'newProjectManager' | 'newSupervisor',
    id: number
  ) => {
    let copy = [...updatedJobReassignments];
    const itemIndex = updatedJobReassignments.findIndex(item => item.jobId === id);

    if (type === 'newProjectManager') {
      copy[itemIndex] = { ...copy[itemIndex], newProjectManager: !!value ? Number(value) : '' };
    } else {
      copy[itemIndex] = { ...copy[itemIndex], newSupervisor: !!value ? Number(value) : '' };
    }
    setUpdatedJobReassignments(copy);
  };

  const handleReassign = async () => {
    setIsSubmitting(true);
    const data = updatedJobReassignments
      .filter(item => !!item.newProjectManager || !!item.newSupervisor)
      .map(item => {
        return {
          jobId: item.jobId,
          projectManagerId: !!item.newProjectManager
            ? Number(item.newProjectManager)
            : item.projectManagerId,
          supervisorId: !!item.newSupervisor ? Number(item.newSupervisor) : item.supervisorId
        };
      });
    try {
      await updateJobReassignments(data);
      enqueueSnackbar('Updated job reassignments successfully!', {
        variant: 'success'
      });
      fetchJobReassignments();
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error updating job reassignments, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (supervisors.length === 0 || projectManagers.length === 0) {
      fetchProjectManagers();
      fetchSupervisors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchJobReassignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false
      },
      {
        Header: 'Start Date',
        accessor: 'frameDate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IJobReassignment } };
        }) => {
          return <>{formatDate(original.frameDate)}</>;
        }
      },
      {
        Header: 'Project Manager',
        accessor: 'projectManagerName',
        sort: false
      },
      {
        Header: 'New PM',
        accessor: 'newProjectManager',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IJobReassignment } };
        }) => {
          return (
            <>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id={`new-pm-${original.jobId}-label`}>
                  {isLoadingProjectManagers ? 'Loading...' : ''}
                </InputLabel>
                <Select
                  displayEmpty={!isLoadingProjectManagers}
                  disabled={isLoadingProjectManagers}
                  name={`new-pm-${original.jobId}`}
                  labelId={`new-pm-${original.jobId}-label`}
                  id={`new-pm-${original.jobId}`}
                  value={
                    updatedJobReassignments.find(item => item.jobId === original.jobId)
                      ?.newProjectManager ?? ''
                  }
                  size="small"
                  onChange={({ target: { value } }) => {
                    handleOnChange(value, 'newProjectManager', original.jobId);
                  }}
                >
                  <MenuItem value="">Leave As Is</MenuItem>
                  {projectManagers &&
                    projectManagers.map((manager, index) => {
                      if (Number(manager.value) !== original.projectManagerId) {
                        return (
                          <MenuItem key={`${manager.value}-${index}`} value={manager.value}>
                            {manager.description}
                          </MenuItem>
                        );
                      } else return null;
                    })}
                </Select>
              </FormControl>
            </>
          );
        }
      },
      {
        Header: 'Supervisor',
        accessor: 'supervisorName',
        sort: false
      },
      {
        Header: 'New Super',
        accessor: 'newSupervisor',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IJobReassignment } };
        }) => {
          return (
            <>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id={`new-super-${original.jobId}-label`}>
                  {isLoadingSupervisors ? 'Loading...' : ''}
                </InputLabel>
                <Select
                  displayEmpty={!isLoadingSupervisors}
                  disabled={isLoadingSupervisors}
                  name={`new-super-${original.jobId}`}
                  labelId={`new-super-${original.jobId}-label`}
                  id={`new-super-${original.jobId}`}
                  value={
                    updatedJobReassignments.find(item => item.jobId === original.jobId)
                      ?.newSupervisor ?? ''
                  }
                  size="small"
                  onChange={({ target: { value } }) => {
                    handleOnChange(value, 'newSupervisor', original.jobId);
                  }}
                >
                  <MenuItem value="">Leave As Is</MenuItem>
                  {supervisors &&
                    supervisors.map((supervisor, index) => {
                      if (Number(supervisor.value) !== original.supervisorId) {
                        return (
                          <MenuItem key={`${supervisor.value}-${index}`} value={supervisor.value}>
                            {supervisor.description}
                          </MenuItem>
                        );
                      } else return null;
                    })}
                </Select>
              </FormControl>
            </>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobReassignments,
    updatedJobReassignments,
    projectManagers,
    isLoadingProjectManagers,
    supervisors,
    isLoadingSupervisors
  ]);

  return (
    <>
      <StaffReassignmentFilters
        isLoading={
          isLoadingJobReassignments ||
          isLoadingProjectManagers ||
          isLoadingSupervisors ||
          isSubmitting
        }
        projectManagers={projectManagers}
        setSelectedProjectManager={setSelectedProjectManager}
        selectedProjectManager={selectedProjectManager}
        supervisors={supervisors}
        setSelectedSupervisor={setSelectedSupervisor}
        selectedSupervisor={selectedSupervisor}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              projectManagerId: undefined,
              supervisorId: undefined
            });
          } else {
            setFilters({
              ...filters,
              projectManagerId: Number(selectedProjectManager) || undefined,
              supervisorId: Number(selectedSupervisor) || undefined
            });
          }
        }}
      />
      <Table
        columns={columns}
        data={jobReassignments}
        isLoading={isLoadingJobReassignments || isSubmitting}
        hidePagination
        noResultsText="No job reassignments to display"
        mobileProps={{
          isLoadingProjectManagers,
          isLoadingSupervisors,
          projectManagers,
          supervisors,
          updatedJobReassignments,
          handleOnChange
        }}
        ResponsiveComponent={isMobile ? MobileStaffReassignment : undefined}
      />
      <Box mt={1} display="flex" alignItems="center">
        <ButtonGroup>
          <Button
            color="primary"
            variant="contained"
            disabled={deepEqual(jobReassignments, updatedJobReassignments) || isSubmitting}
            startIcon={<AssignmentInd />}
            onClick={() => {
              handleReassign();
            }}
          >
            Reassign
          </Button>
          <Button
            disabled={deepEqual(jobReassignments, updatedJobReassignments) || isSubmitting}
            color="inherit"
            variant="contained"
            startIcon={<Replay />}
            onClick={() => {
              setUpdatedJobReassignments(jobReassignments);
            }}
          >
            Reset
          </Button>
        </ButtonGroup>
      </Box>
    </>
  );
};
